import { LanguageProvider } from "@/contexts/LanguageContext";
import { Outlet, useLocation } from "react-router";
import AppBarLayout from "@/components/sidebar/AppBarLayout";
import { SidebarInset, SidebarProvider, SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { FloatingRecordingWidget } from "@/components/audio-recording/FloatingRecordingWidget";
import VoiceRecorderAndUpload from "@/components/audio-recording/VoiceRecorderAndUpload";
import { RecordingControlsProvider, useRecordingControls } from "@/contexts/RecordingControlsContext";
import { RecordingStateProvider, useRecordingState } from "@/contexts/RecordingStateContext";
import { useGetAccountByIdQuery } from "@/redux/api/accounts.rtkq";
import { useAuth } from "@/hooks/useAuth";
import TosGuard from "@/components/TosGuard";
import NotificationBell from "@/components/NotificationBell";
import { Archive } from "lucide-react";
import { canUseApp } from "@/utils/appAccess";
import { cn } from "@/lib/utils";

/** Mobile-only top chrome. Desktop chrome lives inside the sidebar header. */
const AppChromeBar = () => {
  const { openMobile, isMobile } = useSidebar();
  const hideWhileMenuOpen = isMobile && openMobile;

  if (!isMobile) return null;

  return (
    <div
      className={cn(
        "fixed inset-x-0 top-0 z-1000 flex items-center gap-2 border-b border-border/40 bg-indigo-50/90 px-3 py-1.5 pt-[max(0.375rem,env(safe-area-inset-top))] backdrop-blur-sm supports-backdrop-filter:bg-indigo-50/75",
        hideWhileMenuOpen && "pointer-events-none invisible"
      )}
      aria-hidden={hideWhileMenuOpen}
    >
      <SidebarTrigger />
      <NotificationBell placement="mobile" />
    </div>
  );
};

function getSideBarStateCookie() {
  const nameEQ = "sidebar_state" + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length) === "true";
  }
  return undefined;
}

const MainLayoutContent: React.FC = () => {
  const auth = useAuth();
  const location = useLocation();
  const accountId = auth.getAccountId();
  const { data: account, isSuccess } = useGetAccountByIdQuery({ accountId: accountId! }, { skip: !accountId });
  const { stopRecordingRef, continueRecordingRef } = useRecordingControls();
  const { pendingTranscription, setPendingTranscription, isArchiveMode, setIsArchiveMode } = useRecordingState();

  const hasRecordingsApp = isSuccess && canUseApp(account, "recordings");

  const isExpiredTrial =
    isSuccess && account?.subscription.status === "expired";

  const isOnRecordingsPage = location.pathname === "/recordings";

  const handleStopRecording = () => {
    if (stopRecordingRef.current) {
      stopRecordingRef.current();
    }
  };

  const handleContinueRecording = () => {
    continueRecordingRef.current?.();
  };

  return (
    <TosGuard>
      <SidebarProvider defaultOpen={getSideBarStateCookie() ?? true}>
        <AppBarLayout />
        {hasRecordingsApp && !isExpiredTrial && (
          <FloatingRecordingWidget
            onStopRecording={handleStopRecording}
            onContinueRecording={handleContinueRecording}
          />
        )}
        <SidebarInset>
          <AppChromeBar />
          <div className="h-full w-full min-w-0 max-md:pt-16 md:pt-0">
            {/* VoiceRecorderAndUpload stays mounted to preserve recording state */}
            {hasRecordingsApp && (
              <div className={isOnRecordingsPage ? "block" : "hidden"}>
                <div className="relative flex h-125 max-md:h-auto items-center justify-center py-8 max-md:py-4">
                  {isArchiveMode && (
                    <button
                      type="button"
                      onClick={() => setIsArchiveMode(false)}
                      className="absolute top-4 z-10 inline-flex max-w-[calc(100%-2rem)] items-center gap-2 rounded-full border border-amber-300 bg-amber-100 px-4 py-2 text-sm max-md:text-xs font-semibold text-amber-950 shadow-sm transition-colors hover:bg-amber-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
                    >
                      <Archive className="size-4 shrink-0" aria-hidden />
                      <span className="text-start leading-snug">
                        מצב סל המחזור — לחזרה להקלטה ולהעלאה, עבור לתמלולים פעילים
                      </span>
                    </button>
                  )}
                  <div
                    inert={isArchiveMode}
                    aria-disabled={isArchiveMode}
                    className={
                      isArchiveMode
                        ? "pointer-events-none select-none opacity-50 grayscale transition-all duration-200"
                        : "opacity-100 transition-all duration-200"
                    }
                  >
                    <VoiceRecorderAndUpload
                      pendingTranscription={pendingTranscription}
                      setPendingTranscription={setPendingTranscription}
                      isReadOnly={isExpiredTrial}
                      isArchiveMode={isArchiveMode}
                    />
                  </div>
                </div>
              </div>
            )}
            <Outlet />
          </div>
        </SidebarInset>
      </SidebarProvider>
    </TosGuard>
  );
};

const MainLayout: React.FC = () => {
  return (
    <LanguageProvider>
      <RecordingStateProvider>
        <RecordingControlsProvider>
          <MainLayoutContent />
        </RecordingControlsProvider>
      </RecordingStateProvider>
    </LanguageProvider>
  );
};

export default MainLayout;
